/**
 * Plugin SSO para autenticação no ambiente Polgo
 * Permite fazer login SSO em qualquer lugar do projeto informando apenas cliente e ambiente
 */
import Vue from 'vue'
import { mapActions } from 'vuex'

class SSOPlugin {
  constructor(store) {
    this.store = store
    this.loading = false
    this.modalElement = null
    this.backdropElement = null
  }

  /**
   * Realiza login SSO no ambiente Polgo (método genérico)
   * @param {Object} params - Parâmetros para login SSO
   * @param {string} params.cliente - Identificação do cliente
   * @param {string} params.ambiente - Ambiente ('PRODUCAO' ou 'TESTE')
   * @param {string} params.tipo - Tipo de acesso ('dashboard' ou 'pwa')
   * @param {Object} params.options - Opções adicionais
   * @param {boolean} params.options.showLoading - Se deve mostrar loading (padrão: true)
   * @param {boolean} params.options.showErrors - Se deve mostrar erros (padrão: true)
   * @param {Function} params.options.onSuccess - Callback de sucesso
   * @param {Function} params.options.onError - Callback de erro
   * @returns {Promise<Object>} Resultado da operação
   */
  async login({ cliente, ambiente, tipo = 'dashboard', options = {} }) {
    const {
      showLoading = true,
      showErrors = true,
      showFeedback = true,
      onSuccess,
      onError
    } = options

    // Validar parâmetros obrigatórios
    if (!cliente) {
      const error = { status: 400, msg: 'Cliente é obrigatório' }
      if (onError) onError(error)
      if (showErrors) this.showError(error)
      return error
    }

    if (!ambiente || !['PRODUCAO', 'TESTE', 'LOCAL'].includes(ambiente)) {
      const error = { status: 400, msg: 'Ambiente deve ser PRODUCAO, TESTE ou LOCAL' }
      if (onError) onError(error)
      if (showErrors) this.showError(error)
      return error
    }

    if (!tipo || !['dashboard', 'pwa'].includes(tipo)) {
      const error = { status: 400, msg: 'Tipo deve ser dashboard ou pwa' }
      if (onError) onError(error)
      if (showErrors) this.showError(error)
      return error
    }

    // Verificar se já está carregando
    if (this.loading) {
      const error = { status: 429, msg: 'Login SSO já está em andamento' }
      if (onError) onError(error)
      if (showErrors) this.showError(error)
      return error
    }

    try {
      this.loading = true
      
      // Mostrar modal de feedback visual (se habilitado)
      if (showFeedback) {
        this.showFeedbackModal(cliente, ambiente, tipo)
      }

      // Para ambiente LOCAL, usar mesma lógica de TESTE (homologação)
      const ambienteSSO = ambiente === 'LOCAL' ? 'TESTE' : ambiente;

      // Chamar a action do Vuex para iniciar sessão SSO
      const response = await this.store.dispatch('autenticacao/initPolgoApiLoginSSOSession', {
        ambiente: ambienteSSO,
        cliente
      })

      if (response.status === 200 || response.status === 201) {
        const tokenJWT = response.tokenJWT
        const result = { status: response.status, tokenJWT, cliente, ambiente }
        
        // Atualizar modal para sucesso (se habilitado)
        if (showFeedback) {
          this.updateFeedbackModal('success', cliente, ambiente, tipo)
          
          // Aguardar 1.5 segundos para o usuário ver o sucesso
          await this.delay(1500)
          
          // Fechar a modal antes do redirecionamento
          // Isso garante que a modal não fique aberta após o redirecionamento
          this.hideFeedbackModal()
        }
        
        // Redirecionar baseado no tipo
        if (tipo === 'dashboard') {
          this.redirectToDashboard({ ambiente, cliente, tokenJWT })
        } else if (tipo === 'pwa') {
          this.redirectToPWA({ ambiente, cliente, tokenJWT })
        }
        
        // Timeout de segurança para fechar modal se redirecionamento falhar
        // Isso garante que a modal seja fechada mesmo se houver problemas no redirecionamento
        if (showFeedback) {
          setTimeout(() => {
            this.hideFeedbackModal()
          }, 3000) // 3 segundos de timeout
        }
        
        if (onSuccess) onSuccess(result)
        return result
      } else {
        const error = { status: response.status, msg: response.msg }
        
        // Atualizar modal para erro (se habilitado)
        if (showFeedback) {
          this.updateFeedbackModal('error', cliente, ambiente, tipo, error.msg)
          
          // Aguardar um pouco para o usuário ver o erro
          await this.delay(2000)
          
          // Esconder modal
          this.hideFeedbackModal()
        }
        
        if (onError) onError(error)
        if (showErrors) this.showError(error)
        return error
      }
    } catch (error) {
      const errorObj = { 
        status: error.status || 500, 
        msg: error.msg || 'Erro inesperado durante login SSO' 
      }
      
      // Atualizar modal para erro (se habilitado)
      if (showFeedback) {
        this.updateFeedbackModal('error', cliente, ambiente, tipo, errorObj.msg)
        
        // Aguardar um pouco para o usuário ver o erro
        await this.delay(2000)
        
        // Esconder modal
        this.hideFeedbackModal()
      }
      
      if (onError) onError(errorObj)
      if (showErrors) this.showError(errorObj)
      return errorObj
    } finally {
      this.loading = false
    }
  }

  /**
   * Redireciona para o dashboard do Polgo
   * @param {Object} params - Parâmetros de redirecionamento
   * @param {string} params.ambiente - Ambiente ('PRODUCAO' ou 'TESTE')
   * @param {string} params.cliente - Identificação do cliente
   * @param {string} params.tokenJWT - Token JWT para autenticação
   * @param {Object} params.options - Opções adicionais
   * @param {boolean} params.options.newTab - Se deve abrir em nova aba (padrão: true)
   * @param {string} params.options.target - Target da janela (padrão: '_blank')
   */
  redirectToDashboard({ ambiente, cliente, tokenJWT, options = {} }) {
    const { newTab = true, target = '_blank' } = options

    let dashboardUrl
    if (ambiente === 'PRODUCAO') {
      dashboardUrl = `https://dash.polgo.com.br/login?cliente=${cliente}&token=${tokenJWT}`
    } else if (ambiente === 'LOCAL') {
      dashboardUrl = `http://localhost:8080/login?cliente=${cliente}&token=${tokenJWT}`
    } else {
      dashboardUrl = `https://testedash.polgo.com.br/login?cliente=${cliente}&token=${tokenJWT}`
    }

    if (newTab) {
      window.open(dashboardUrl, target)
    } else {
      window.location.href = dashboardUrl
    }
  }

  /**
   * Realiza login SSO para Dashboard
   * @param {Object} params - Parâmetros para login SSO
   * @param {string} params.cliente - Identificação do cliente
   * @param {string} params.ambiente - Ambiente ('PRODUCAO' ou 'TESTE')
   * @param {Object} params.options - Opções adicionais
   * @param {boolean} params.options.showLoading - Se deve mostrar loading (padrão: true)
   * @param {boolean} params.options.showErrors - Se deve mostrar erros (padrão: true)
   * @param {boolean} params.options.showFeedback - Se deve mostrar modal de feedback (padrão: true)
   * @param {Function} params.options.onSuccess - Callback de sucesso
   * @param {Function} params.options.onError - Callback de erro
   * @returns {Promise<Object>} Resultado da operação
   */
  async loginDashboard({ cliente, ambiente, options = {} }) {
    return this.login({ cliente, ambiente, tipo: 'dashboard', options })
  }

  /**
   * Redireciona para PWA (sem SSO, apenas busca URL e redireciona)
   * @param {Object} params - Parâmetros para redirecionamento PWA
   * @param {string} params.cliente - Identificação do cliente
   * @param {string} params.ambiente - Ambiente ('PRODUCAO' ou 'TESTE')
   * @param {Object} params.options - Opções adicionais
   * @param {boolean} params.options.showLoading - Se deve mostrar loading (padrão: true)
   * @param {boolean} params.options.showErrors - Se deve mostrar erros (padrão: true)
   * @param {boolean} params.options.showFeedback - Se deve mostrar modal de feedback (padrão: true)
   * @param {Function} params.options.onSuccess - Callback de sucesso
   * @param {Function} params.options.onError - Callback de erro
   * @returns {Promise<Object>} Resultado da operação
   */
  async loginPWA({ cliente, ambiente, options = {} }) {
    // Validar parâmetros obrigatórios
    if (!cliente) {
      const error = { status: 400, msg: 'Cliente é obrigatório' }
      if (options.onError) options.onError(error)
      if (options.showErrors !== false) this.showError(error)
      return error
    }

    if (!ambiente || !['PRODUCAO', 'TESTE', 'LOCAL'].includes(ambiente)) {
      const error = { status: 400, msg: 'Ambiente deve ser PRODUCAO, TESTE ou LOCAL' }
      if (options.onError) options.onError(error)
      if (options.showErrors !== false) this.showError(error)
      return error
    }

    // Verificar se já está carregando
    if (this.loading) {
      const error = { status: 429, msg: 'Redirecionamento PWA já está em andamento' }
      if (options.onError) options.onError(error)
      if (options.showErrors !== false) this.showError(error)
      return error
    }

    try {
      this.loading = true
      
      // Mostrar modal de feedback visual (se habilitado)
      if (options.showFeedback !== false) {
        this.showFeedbackModal(cliente, ambiente, 'pwa')
      }

      // Para ambiente LOCAL, usar URL local com configID
      if (ambiente === 'LOCAL') {
        console.log('🔍 Buscando ID da configuração PWA para ambiente local:', cliente);
        
        let configId;
        try {
          // Buscar configuração PWAKRAKEN para obter o ID
          const configPWAKRAKEN = await this.store.dispatch('configuracoes/fetchSettingByClienteENome', { 
            cliente: cliente, 
            nome: 'PWAKRAKEN' 
          });

          console.log('Resposta completa da configuração:', configPWAKRAKEN);

          if (configPWAKRAKEN.status === 200 && configPWAKRAKEN.dados) {
            // Tentar pegar o ID de diferentes possíveis campos
            configId = configPWAKRAKEN.dados._id || configPWAKRAKEN.dados.id || configPWAKRAKEN.id;
            
            if (!configId) {
              console.error('Estrutura da configuração:', configPWAKRAKEN);
              throw new Error('ID da configuração não encontrado na resposta');
            }
            
            console.log('ID da configuração encontrado:', configId);
          } else {
            throw new Error('Configuração PWAKRAKEN não encontrada ou resposta inválida');
          }
        } catch (error) {
          console.error('Erro ao buscar configuração PWAKRAKEN:', error);
          const errorObj = {
            status: 404,
            msg: `Não foi possível encontrar a configuração PWAKRAKEN para o cliente "${cliente}". Verifique se a configuração existe.`,
            tipo: 'SEM_CONFIGURACAO',
            cliente
          };
          
          if (options.showFeedback !== false) {
            this.updateFeedbackModal('error', cliente, ambiente, 'pwa', errorObj.msg)
            await this.delay(2000)
            this.hideFeedbackModal()
          }
          
          if (options.onError) options.onError(errorObj)
          if (options.showErrors !== false) this.showError(errorObj)
          return errorObj;
        }

        // Construir URL local com configID e ambiente
        const pwaUrl = `http://127.0.0.1:5173/?configID=${configId}&ambiente=dev`;
        const result = { status: 200, cliente, ambiente, pwaUrl }
        
        // Atualizar modal para sucesso
        if (options.showFeedback !== false) {
          this.updateFeedbackModal('success', cliente, ambiente, 'pwa')
          await this.delay(1500)
          this.hideFeedbackModal()
        }
        
        // Redirecionar para a PWA local
        console.log(`Redirecionando para PWA local:`, pwaUrl);
        const { newTab = true, target = '_blank' } = options
        if (newTab) {
          window.open(pwaUrl, target)
        } else {
          window.location.href = pwaUrl
        }
        
        if (options.showFeedback !== false) {
          setTimeout(() => {
            this.hideFeedbackModal()
          }, 3000)
        }
        
        if (options.onSuccess) options.onSuccess(result)
        return result
      }

      // Buscar configuração da PWA
      console.log('🔍 Buscando configuração PWA para:', cliente);
      let pwaUrls;
      try {
        pwaUrls = await this.buscarConfiguracaoPWA(cliente);
      } catch (configError) {
        // Tratar erro de configuração
        const error = {
          status: configError.status,
          msg: configError.msg,
          tipo: configError.tipo,
          cliente: configError.cliente
        };
        
        // Atualizar modal para erro (se habilitado)
        if (options.showFeedback !== false) {
          this.updateFeedbackModal('error', cliente, ambiente, 'pwa', error.msg)
          await this.delay(2000)
          this.hideFeedbackModal()
        }
        
        if (options.onError) options.onError(error)
        if (options.showErrors !== false) {
          // Mostrar modal apenas se a configuração existe mas tem problemas
          if (error.tipo !== 'SEM_CONFIGURACAO') {
            setTimeout(() => {
              console.log('Chamando showConfigError após timeout (erro de configuração)');
              this.showConfigError(error)
            }, 100)
          }
        }
        return error
      }
      
      // Mapear ambiente para chave
      const ambienteKey = ambiente === 'PRODUCAO' ? 'prod' : 'test';
      const pwaUrl = pwaUrls[ambienteKey];
      
      if (!pwaUrl) {
        const ambienteLabel = ambiente === 'PRODUCAO' ? 'produção' : 'homologação';
        const error = { 
          status: 404, 
          msg: `Não foi possível encontrar URL configurada para o cliente "${cliente}" no ambiente ${ambienteLabel}. Verifique as configurações PWAKRAKEN.`,
          tipo: 'URL_NAO_ENCONTRADA',
          cliente,
          ambiente
        }
        
        // Atualizar modal para erro (se habilitado)
        if (options.showFeedback !== false) {
          this.updateFeedbackModal('error', cliente, ambiente, 'pwa', error.msg)
          await this.delay(2000)
          this.hideFeedbackModal()
        }
        
        if (options.onError) options.onError(error)
        if (options.showErrors !== false) {
          // Mostrar modal apenas se a configuração existe mas tem problemas
          if (error.tipo !== 'SEM_CONFIGURACAO') {
            setTimeout(() => {
              console.log('Chamando showConfigError após timeout (URL não encontrada)');
              this.showConfigError(error)
            }, 100)
          }
        }
        return error
      }

      const result = { status: 200, cliente, ambiente, pwaUrls, pwaUrl }
      
      // Atualizar modal para sucesso (se habilitado)
      if (options.showFeedback !== false) {
        this.updateFeedbackModal('success', cliente, ambiente, 'pwa')
        
        // Aguardar 1.5 segundos para o usuário ver o sucesso
        await this.delay(1500)
        
        // Fechar a modal antes do redirecionamento
        this.hideFeedbackModal()
      }
      
      // Redirecionar para a PWA
      console.log(`Redirecionando para PWA (${ambiente}):`, pwaUrl);
      
      const { newTab = true, target = '_blank' } = options
      if (newTab) {
        window.open(pwaUrl, target)
      } else {
        window.location.href = pwaUrl
      }
      
      // Timeout de segurança para fechar modal se redirecionamento falhar
      if (options.showFeedback !== false) {
        setTimeout(() => {
          this.hideFeedbackModal()
        }, 3000) // 3 segundos de timeout
      }
      
      if (options.onSuccess) options.onSuccess(result)
      return result
      
    } catch (error) {
      const errorObj = { 
        status: error.status || 500, 
        msg: error.msg || 'Erro inesperado ao buscar configuração PWA',
        tipo: 'ERRO_INESPERADO',
        cliente
      }
      
      // Atualizar modal para erro (se habilitado)
      if (options.showFeedback !== false) {
        this.updateFeedbackModal('error', cliente, ambiente, 'pwa', errorObj.msg)
        
        // Aguardar um pouco para o usuário ver o erro
        await this.delay(2000)
        
        // Esconder modal
        this.hideFeedbackModal()
      }
      
      if (options.onError) options.onError(errorObj)
      if (options.showErrors !== false) {
        // Mostrar modal apenas se a configuração existe mas tem problemas
        if (errorObj.tipo !== 'SEM_CONFIGURACAO') {
          setTimeout(() => {
            console.log('Chamando showConfigError após timeout (erro inesperado)');
            this.showConfigError(errorObj)
          }, 100)
        }
      }
      return errorObj
    } finally {
      this.loading = false
    }
  }

  /**
   * Redireciona para Landing Page (busca URL na configuração PWAKRAKEN)
   * @param {Object} params - Parâmetros para redirecionamento LP
   * @param {string} params.cliente - Identificação do cliente
   * @param {Object} params.options - Opções adicionais
   * @param {boolean} params.options.showLoading - Se deve mostrar loading (padrão: true)
   * @param {boolean} params.options.showErrors - Se deve mostrar erros (padrão: true)
   * @param {boolean} params.options.showFeedback - Se deve mostrar modal de feedback (padrão: true)
   * @param {Function} params.options.onSuccess - Callback de sucesso
   * @param {Function} params.options.onError - Callback de erro
   * @returns {Promise<Object>} Resultado da operação
   */
  async loginLP({ cliente, options = {} }) {
    // Validar parâmetros obrigatórios
    if (!cliente) {
      const error = { status: 400, msg: 'Cliente é obrigatório' }
      if (options.onError) options.onError(error)
      if (options.showErrors !== false) this.showError(error)
      return error
    }

    // Verificar se já está carregando
    if (this.loading) {
      const error = { status: 429, msg: 'Redirecionamento LP já está em andamento' }
      if (options.onError) options.onError(error)
      if (options.showErrors !== false) this.showError(error)
      return error
    }

    try {
      this.loading = true
      
      // Mostrar modal de feedback visual (se habilitado)
      if (options.showFeedback !== false) {
        this.showFeedbackModal(cliente, 'PRODUCAO', 'lp')
      }

      // Buscar configuração da LP
      console.log('🔍 Buscando configuração LP para:', cliente);
      let lpUrl;
      try {
        lpUrl = await this.buscarConfiguracaoLP(cliente);
      } catch (configError) {
        // Tratar erro de configuração
        const error = {
          status: configError.status,
          msg: configError.msg,
          tipo: configError.tipo,
          cliente: configError.cliente
        };
        
        // Atualizar modal para erro (se habilitado)
        if (options.showFeedback !== false) {
          this.updateFeedbackModal('error', cliente, 'PRODUCAO', 'lp', error.msg)
          await this.delay(2000)
          this.hideFeedbackModal()
        }
        
        if (options.onError) options.onError(error)
        if (options.showErrors !== false) {
          // Mostrar modal apenas se a configuração existe mas tem problemas
          if (error.tipo !== 'SEM_CONFIGURACAO') {
            setTimeout(() => {
              console.log('Chamando showConfigError após timeout (erro de configuração LP)');
              this.showConfigError(error)
            }, 100)
          }
        }
        return error
      }

      const result = { status: 200, cliente, lpUrl }
      
      // Atualizar modal para sucesso (se habilitado)
      if (options.showFeedback !== false) {
        this.updateFeedbackModal('success', cliente, 'PRODUCAO', 'lp')
        
        // Aguardar 1.5 segundos para o usuário ver o sucesso
        await this.delay(1500)
        
        // Fechar a modal antes do redirecionamento
        this.hideFeedbackModal()
      }
      
      // Redirecionar para a LP
      console.log(`Redirecionando para LP:`, lpUrl);
      
      const { newTab = true, target = '_blank' } = options
      if (newTab) {
        window.open(lpUrl, target)
      } else {
        window.location.href = lpUrl
      }
      
      // Timeout de segurança para fechar modal se redirecionamento falhar
      if (options.showFeedback !== false) {
        setTimeout(() => {
          this.hideFeedbackModal()
        }, 3000) // 3 segundos de timeout
      }
      
      if (options.onSuccess) options.onSuccess(result)
      return result
      
    } catch (error) {
      const errorObj = { 
        status: error.status || 500, 
        msg: error.msg || 'Erro inesperado ao buscar configuração LP',
        tipo: 'ERRO_INESPERADO',
        cliente
      }
      
      // Atualizar modal para erro (se habilitado)
      if (options.showFeedback !== false) {
        this.updateFeedbackModal('error', cliente, 'PRODUCAO', 'lp', errorObj.msg)
        
        // Aguardar um pouco para o usuário ver o erro
        await this.delay(2000)
        
        // Esconder modal
        this.hideFeedbackModal()
      }
      
      if (options.onError) options.onError(errorObj)
      if (options.showErrors !== false) {
        // Mostrar modal apenas se a configuração existe mas tem problemas
        if (errorObj.tipo !== 'SEM_CONFIGURACAO') {
          setTimeout(() => {
            console.log('Chamando showConfigError após timeout (erro inesperado LP)');
            this.showConfigError(errorObj)
          }, 100)
        }
      }
      return errorObj
    } finally {
      this.loading = false
    }
  }

  /**
   * Busca a configuração PWAKRAKEN do cliente e extrai a URL da Landing Page
   * @param {string} cliente - Identificação do cliente
   * @returns {Promise<string>} URL da Landing Page
   */
  async buscarConfiguracaoLP(cliente) {
    try {
      console.log('🔍 Buscando configuração LP PWAKRAKEN para:', cliente);
      
      // Buscar configuração PWAKRAKEN do cliente
      const configPWAKRAKEN = await this.store.dispatch('configuracoes/fetchSettingByClienteENome', { 
        cliente: cliente, 
        nome: 'PWAKRAKEN' 
      });

      // Se não tem PWAKRAKEN, lançar erro
      if (configPWAKRAKEN.status !== 200 || !configPWAKRAKEN.dados) {
        const error = {
          status: 404,
          msg: `Cliente "${cliente}" não possui configuração PWAKRAKEN. É necessário configurar a URL da Landing Page para este cliente.`,
          tipo: 'SEM_CONFIGURACAO',
          cliente
        };
        throw error;
      }

      // Extrair APP_URL_LANDING_PAGE da configuração
      const lpUrlEntry = configPWAKRAKEN.dados.entradas.find(e => e.chave === 'APP_URL_LANDING_PAGE');
      if (!lpUrlEntry) {
        const error = {
          status: 404,
          msg: `Cliente "${cliente}" possui configuração PWAKRAKEN, mas não encontrou a chave APP_URL_LANDING_PAGE. Verifique as configurações.`,
          tipo: 'SEM_APP_URL_LANDING_PAGE',
          cliente
        };
        throw error;
      }

      // Validar se a URL não está vazia ou inválida
      const lpUrl = lpUrlEntry.valor;
      if (!lpUrl || lpUrl.trim() === '' || lpUrl.toLowerCase() === 'vazio' || lpUrl.toLowerCase() === 'null') {
        const error = {
          status: 400,
          msg: `URL da Landing Page do cliente "${cliente}" está vazia ou inválida. Verifique as configurações.`,
          tipo: 'URL_LANDING_PAGE_INVALIDA',
          cliente
        };
        throw error;
      }

      // Formatar a URL
      const formattedUrl = this.formatarUrlLP(lpUrl.trim());
      console.log('URL LP encontrada e formatada:', formattedUrl);
      
      return formattedUrl;
    } catch (error) {
      console.error('Erro ao buscar configuração LP:', error);
      throw error;
    }
  }

  /**
   * Busca a configuração PWAKRAKEN do cliente e extrai as URLs da PWA
   * @param {string} cliente - Identificação do cliente
   * @returns {Promise<Object>} URLs da PWA por ambiente
   */
  async buscarConfiguracaoPWA(cliente) {
    try {
      console.log('🔍 Buscando configuração PWAKRAKEN para:', cliente);
      
      // Buscar configuração PWAKRAKEN do cliente
      const configPWAKRAKEN = await this.store.dispatch('configuracoes/fetchSettingByClienteENome', { 
        cliente: cliente, 
        nome: 'PWAKRAKEN' 
      });

      // Se não tem PWAKRAKEN, lançar erro
      if (configPWAKRAKEN.status !== 200 || !configPWAKRAKEN.dados) {
        const error = {
          status: 404,
          msg: `Cliente "${cliente}" não possui configuração PWAKRAKEN. É necessário configurar as URLs da PWA para este cliente.`,
          tipo: 'SEM_CONFIGURACAO',
          cliente
        };
        throw error;
      }

      // Extrair APP_URLS da configuração
      const appUrlsEntry = configPWAKRAKEN.dados.entradas.find(e => e.chave === 'APP_URLS');
      if (!appUrlsEntry) {
        const error = {
          status: 404,
          msg: `Cliente "${cliente}" possui configuração PWAKRAKEN, mas não encontrou a chave APP_URLS. Verifique as configurações.`,
          tipo: 'SEM_APP_URLS',
          cliente
        };
        throw error;
      }

      // Fazer parse do APP_URLS
      let appUrls;
      try {
        appUrls = JSON.parse(appUrlsEntry.valor);
        console.log('APP_URLS parseado com sucesso:', appUrls);
      } catch (e) {
        console.error('Erro ao fazer parse do APP_URLS:', e);
        const error = {
          status: 400,
          msg: `Erro ao processar configuração APP_URLS do cliente "${cliente}". Verifique se o formato está correto.`,
          tipo: 'ERRO_PARSE',
          cliente
        };
        throw error;
      }

      // Extrair URLs por ambiente
      const prodUrls = appUrls.filter(u => u.ambiente === 'prod').map(u => u.url);
      const testUrls = appUrls.filter(u => u.ambiente === 'teste').map(u => u.url);

      const urls = {};
      
      if (prodUrls.length > 0) {
        const selectedProdUrl = this.selecionarMelhorUrl(prodUrls);
        urls.prod = this.formatarUrlPWA(selectedProdUrl);
        console.log('URL produção selecionada:', urls.prod, 'de', prodUrls.length, 'opções');
      }
      
      if (testUrls.length > 0) {
        const selectedTestUrl = this.selecionarMelhorUrl(testUrls);
        urls.test = this.formatarUrlPWA(selectedTestUrl);
        console.log('URL teste selecionada:', urls.test, 'de', testUrls.length, 'opções');
      }

      console.log('URLs finais da PWA:', urls);
      return urls;
    } catch (error) {
      console.error('Erro ao buscar configuração PWA:', error);
      throw error;
    }
  }

  /**
   * Redireciona para a PWA do Polgo (método interno para uso com SSO)
   * @param {Object} params - Parâmetros de redirecionamento
   * @param {string} params.ambiente - Ambiente ('PRODUCAO' ou 'TESTE')
   * @param {string} params.cliente - Identificação do cliente
   * @param {string} params.tokenJWT - Token JWT para autenticação
   * @param {Object} params.options - Opções adicionais
   * @param {boolean} params.options.newTab - Se deve abrir em nova aba (padrão: true)
   * @param {string} params.options.target - Target da janela (padrão: '_blank')
   */
  async redirectToPWA({ ambiente, cliente, tokenJWT, options = {} }) {
    const { newTab = true, target = '_blank' } = options

    // Buscar configuração específica da PWA do cliente
    const pwaUrls = await this.buscarConfiguracaoPWA(cliente);
    
    // Mapear ambiente para chave
    const ambienteKey = ambiente === 'PRODUCAO' ? 'prod' : 'test';
    let pwaUrl = pwaUrls[ambienteKey];
    
    // Adicionar token JWT se disponível
    if (tokenJWT) {
      const separator = pwaUrl.includes('?') ? '&' : '?';
      pwaUrl += `${separator}token=${tokenJWT}`;
    }

    console.log(`Redirecionando para PWA (${ambiente}):`, pwaUrl);

    if (newTab) {
      window.open(pwaUrl, target)
    } else {
      window.location.href = pwaUrl
    }
  }

  /**
   * Verifica se o plugin está carregando
   * @returns {boolean} True se estiver carregando
   */
  isLoading() {
    return this.loading
  }

  /**
   * Cria e mostra a modal de feedback visual
   * @param {string} cliente - Nome do cliente
   * @param {string} ambiente - Ambiente (PRODUCAO ou TESTE)
   * @param {string} tipo - Tipo de acesso (dashboard ou pwa)
   */
  showFeedbackModal(cliente, ambiente, tipo = 'dashboard') {
    // Criar backdrop
    this.backdropElement = document.createElement('div')
    this.backdropElement.className = 'sso-feedback-backdrop'
    this.backdropElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      z-index: 9999;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: sso-fadeIn 0.3s ease-in-out;
    `

    // Criar modal
    this.modalElement = document.createElement('div')
    this.modalElement.className = 'sso-feedback-modal'
    this.modalElement.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 30px;
      max-width: 400px;
      width: 90%;
      text-align: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      animation: sso-slideIn 0.3s ease-out;
    `

    // Conteúdo da modal
    const tipoLabel = tipo === 'dashboard' ? 'Dashboard' : tipo === 'pwa' ? 'PWA' : tipo === 'lp' ? 'Landing Page' : 'Aplicação'
    const loadingText = tipo === 'dashboard' ? 'Autenticando...' : tipo === 'lp' ? 'Buscando URL...' : 'Buscando configuração...'
    const connectingText = tipo === 'dashboard' ? 'Conectando ao ambiente' : tipo === 'lp' ? 'Obtendo URL da' : 'Preparando redirecionamento para'
    const ambienteLabel = ambiente === 'PRODUCAO' ? 'Produção' : ambiente === 'LOCAL' ? 'Local (dev)' : 'Homologação'
    
    this.modalElement.innerHTML = `
      <div class="sso-loading-container">
        <div class="sso-spinner">
          <div class="sso-spinner-ring"></div>
        </div>
        <h3 style="margin: 20px 0 10px 0; color: #333; font-size: 18px;">
          ${loadingText}
        </h3>
        <p style="margin: 0 0 15px 0; color: #666; font-size: 14px;">
          ${connectingText} <strong>${ambienteLabel}</strong>
        </p>
        <p style="margin: 0; color: #888; font-size: 12px;">
          Cliente: <strong>${cliente}</strong> | ${tipoLabel}
        </p>
      </div>
    `

    // Adicionar estilos CSS
    this.addFeedbackStyles()

    // Adicionar ao DOM
    this.backdropElement.appendChild(this.modalElement)
    document.body.appendChild(this.backdropElement)
  }

  /**
   * Atualiza a modal de feedback com o status
   * @param {string} status - 'success' ou 'error'
   * @param {string} cliente - Nome do cliente
   * @param {string} ambiente - Ambiente
   * @param {string} tipo - Tipo de acesso (dashboard ou pwa)
   * @param {string} errorMessage - Mensagem de erro (opcional)
   */
  updateFeedbackModal(status, cliente, ambiente, tipo = 'dashboard', errorMessage = '') {
    if (!this.modalElement) return

    const isSuccess = status === 'success'
    const icon = isSuccess ? '✅' : '❌'
    const tipoLabel = tipo === 'dashboard' ? 'Dashboard' : tipo === 'pwa' ? 'PWA' : 'Landing Page'
    
    // Títulos e mensagens específicas por tipo
    let title, message;
    if (tipo === 'dashboard') {
      title = isSuccess ? 'Autenticação Realizada!' : 'Erro na Autenticação'
      message = isSuccess 
        ? `Redirecionando para o ${tipoLabel.toLowerCase()}...`
        : errorMessage || 'Ocorreu um erro durante a autenticação'
    } else if (tipo === 'pwa') {
      title = isSuccess ? 'Redirecionamento Realizado!' : 'Erro no Redirecionamento'
      message = isSuccess 
        ? `Redirecionando para a ${tipoLabel.toLowerCase()}...`
        : errorMessage || 'Ocorreu um erro durante o redirecionamento'
    } else if (tipo === 'lp') {
      title = isSuccess ? 'URL Obtida com Sucesso!' : 'Erro ao Obter URL'
      message = isSuccess 
        ? `URL da ${tipoLabel} obtida com sucesso!`
        : errorMessage || 'Ocorreu um erro ao obter a URL da Landing Page'
    } else {
      title = isSuccess ? 'Redirecionamento Realizado!' : 'Erro no Redirecionamento'
      message = isSuccess 
        ? `Redirecionando para a ${tipoLabel.toLowerCase()}...`
        : errorMessage || 'Ocorreu um erro durante o redirecionamento'
    }

    this.modalElement.innerHTML = `
      <div class="sso-result-container">
        <div class="sso-icon" style="font-size: 48px; margin-bottom: 20px;">
          ${icon}
        </div>
        <h3 style="margin: 0 0 10px 0; color: ${isSuccess ? '#28a745' : '#dc3545'}; font-size: 18px;">
          ${title}
        </h3>
        <p style="margin: 0 0 15px 0; color: #666; font-size: 14px;">
          ${message}
        </p>
        <p style="margin: 0; color: #888; font-size: 12px;">
          Cliente: <strong>${cliente}</strong> | Ambiente: <strong>${ambiente === 'PRODUCAO' ? 'Produção' : ambiente === 'LOCAL' ? 'Local (dev)' : 'Homologação'}</strong> | ${tipoLabel}
        </p>
      </div>
    `
  }

  /**
   * Esconde a modal de feedback
   */
  hideFeedbackModal() {
    if (this.backdropElement) {
      this.backdropElement.style.animation = 'sso-fadeOut 0.3s ease-in-out'
      setTimeout(() => {
        if (this.backdropElement && this.backdropElement.parentNode) {
          this.backdropElement.parentNode.removeChild(this.backdropElement)
        }
        this.backdropElement = null
        this.modalElement = null
      }, 300)
    }
  }

  /**
   * Adiciona os estilos CSS necessários para a modal
   */
  addFeedbackStyles() {
    if (document.getElementById('sso-feedback-styles')) return

    const styleElement = document.createElement('style')
    styleElement.id = 'sso-feedback-styles'
    styleElement.textContent = `
      @keyframes sso-fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      @keyframes sso-fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }
      
      @keyframes sso-slideIn {
        from { 
          opacity: 0;
          transform: translateY(-20px) scale(0.95);
        }
        to { 
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }
      
      @keyframes sso-spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
      
      .sso-spinner {
        display: inline-block;
        position: relative;
        width: 60px;
        height: 60px;
      }
      
      .sso-spinner-ring {
        position: absolute;
        width: 100%;
        height: 100%;
        border: 4px solid #f3f3f3;
        border-top: 4px solid #007bff;
        border-radius: 50%;
        animation: sso-spin 1s linear infinite;
      }
      
      .sso-feedback-backdrop {
        backdrop-filter: blur(2px);
      }
      
      .sso-feedback-modal {
        position: relative;
        overflow: hidden;
      }
      
      .sso-feedback-modal::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, #007bff, #28a745);
        animation: sso-progress 2s ease-in-out infinite;
      }
      
      @keyframes sso-progress {
        0% { transform: translateX(-100%); }
        100% { transform: translateX(100%); }
      }
    `
    document.head.appendChild(styleElement)
  }

  /**
   * Função de delay para aguardar
   * @param {number} ms - Milissegundos para aguardar
   * @returns {Promise} Promise que resolve após o delay
   */
  delay(ms) {
    return new Promise(resolve => setTimeout(resolve, ms))
  }

  /**
   * Mostra erro usando SweetAlert2
   * @param {Object} error - Objeto de erro
   * @param {number} error.status - Status do erro
   * @param {string} error.msg - Mensagem do erro
   */
  showError(error) {
    // Verificar se o SweetAlert2 está disponível
    if (typeof this.store.$swal !== 'undefined') {
      this.store.$swal({
        type: 'error',
        title: 'Ops...',
        text: error.msg,
      })
    } else {
      console.error('SSO Plugin Error:', error)
      alert(`Erro: ${error.msg}`)
    }
  }

  /**
   * Mostra erro de configuração com opção de revisar configuração
   * @param {Object} error - Objeto de erro de configuração
   * @param {number} error.status - Status do erro
   * @param {string} error.msg - Mensagem do erro
   * @param {string} error.tipo - Tipo do erro
   * @param {string} error.cliente - Cliente afetado
   */
  showConfigError(error) {
    console.log('=== INÍCIO showConfigError ===');
    console.log('Erro recebido:', error);
    
    // Criar modal customizada para erro de configuração
    this.showConfigErrorModal(error);
    
    console.log('=== FIM showConfigError ===');
  }

  /**
   * Mostra modal customizada para erro de configuração
   * @param {Object} error - Objeto de erro
   */
  showConfigErrorModal(error) {
    // Criar backdrop
    const backdropElement = document.createElement('div')
    backdropElement.className = 'sso-config-error-backdrop'
    backdropElement.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      z-index: 10000;
      display: flex;
      align-items: center;
      justify-content: center;
      animation: sso-fadeIn 0.3s ease-in-out;
    `

    // Criar modal
    const modalElement = document.createElement('div')
    modalElement.className = 'sso-config-error-modal'
    modalElement.style.cssText = `
      background: white;
      border-radius: 12px;
      padding: 30px;
      max-width: 500px;
      width: 90%;
      text-align: center;
      box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
      animation: sso-slideIn 0.3s ease-out;
    `

    // Conteúdo da modal
    modalElement.innerHTML = `
      <div class="sso-config-error-container">
        <div class="sso-icon" style="font-size: 48px; margin-bottom: 20px; color: #ffc107;">
          ⚠️
        </div>
        <h3 style="margin: 0 0 15px 0; color: #dc3545; font-size: 20px;">
          Configuração PWA Necessária
        </h3>
        <div style="text-align: left; margin-bottom: 25px;">
          <p style="margin-bottom: 15px; color: #333; font-size: 16px;">
            <strong>${error.msg}</strong>
          </p>
          <p style="font-size: 14px; color: #666; margin-bottom: 0;">
            Para resolver este problema, é necessário configurar as URLs da PWA para o cliente <strong>${error.cliente}</strong>.
          </p>
        </div>
        <div style="display: flex; gap: 10px; justify-content: center;">
          <button id="sso-config-edit-btn" style="
            background: #007bff;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
          ">Revisar configuração da PWAKRAKEN</button>
          <button id="sso-config-close-btn" style="
            background: #6c757d;
            color: white;
            border: none;
            padding: 12px 24px;
            border-radius: 6px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
          ">Fechar</button>
        </div>
      </div>
    `

    // Adicionar ao DOM
    backdropElement.appendChild(modalElement)
    document.body.appendChild(backdropElement)

    // Event listeners
    const editBtn = modalElement.querySelector('#sso-config-edit-btn')
    const closeBtn = modalElement.querySelector('#sso-config-close-btn')

    editBtn.addEventListener('click', () => {
      this.abrirEdicaoConfiguracao(error.cliente)
      this.hideConfigErrorModal(backdropElement)
    })

    closeBtn.addEventListener('click', () => {
      this.hideConfigErrorModal(backdropElement)
    })

    // Fechar ao clicar no backdrop
    backdropElement.addEventListener('click', (e) => {
      if (e.target === backdropElement) {
        this.hideConfigErrorModal(backdropElement)
      }
    })
  }

  /**
   * Esconde a modal de erro de configuração
   * @param {HTMLElement} backdropElement - Elemento backdrop
   */
  hideConfigErrorModal(backdropElement) {
    if (backdropElement) {
      backdropElement.style.animation = 'sso-fadeOut 0.3s ease-in-out'
      setTimeout(() => {
        if (backdropElement && backdropElement.parentNode) {
          backdropElement.parentNode.removeChild(backdropElement)
        }
      }, 300)
    }
  }

  /**
   * Abre a página de edição direta da configuração PWAKRAKEN
   * @param {string} cliente - Nome do cliente
   */
  abrirEdicaoConfiguracao(cliente) {
    // Construir URL para a página de edição direta
    const editUrl = `/configuracoes/editar/${encodeURIComponent(cliente)}/PWAKRAKEN`;
    
    // Abrir em nova aba
    window.open(editUrl, '_blank');
  }

  /**
   * Seleciona a melhor URL de uma lista de URLs, priorizando URLs não locais
   * @param {Array<string>} urls - Lista de URLs para escolher
   * @returns {string} Melhor URL selecionada
   */
  selecionarMelhorUrl(urls) {
    if (!urls || urls.length === 0) {
      return null;
    }

    if (urls.length === 1) {
      return urls[0];
    }

    // Separar URLs em categorias
    const urlsExternas = [];
    const urlsLocais = [];

    urls.forEach(url => {
      if (!url || typeof url !== 'string') {
        return;
      }

      const urlLower = url.toLowerCase();
      
      // Verificar se é URL local
      const isLocal = urlLower.includes('localhost') || 
                     urlLower.includes('127.0.0.1') || 
                     urlLower.includes('192.168.') ||
                     urlLower.includes('10.') ||
                     urlLower.includes('172.16.') ||
                     urlLower.includes('172.17.') ||
                     urlLower.includes('172.18.') ||
                     urlLower.includes('172.19.') ||
                     urlLower.includes('172.20.') ||
                     urlLower.includes('172.21.') ||
                     urlLower.includes('172.22.') ||
                     urlLower.includes('172.23.') ||
                     urlLower.includes('172.24.') ||
                     urlLower.includes('172.25.') ||
                     urlLower.includes('172.26.') ||
                     urlLower.includes('172.27.') ||
                     urlLower.includes('172.28.') ||
                     urlLower.includes('172.29.') ||
                     urlLower.includes('172.30.') ||
                     urlLower.includes('172.31.');

      if (isLocal) {
        urlsLocais.push(url);
      } else {
        urlsExternas.push(url);
      }
    });

    // Priorizar URLs externas
    if (urlsExternas.length > 0) {
      console.log(`Selecionando URL externa de ${urlsExternas.length} opções:`, urlsExternas);
      return urlsExternas[0]; // Retorna a primeira URL externa
    }

    // Se não há URLs externas, usar a primeira URL local
    if (urlsLocais.length > 0) {
      console.log(`Usando URL local de ${urlsLocais.length} opções:`, urlsLocais);
      return urlsLocais[0];
    }

    // Fallback: retornar a primeira URL da lista original
    console.log('Usando primeira URL da lista como fallback:', urls[0]);
    return urls[0];
  }

  /**
   * Formata URL da Landing Page para garantir formato correto
   * @param {string} url - URL original
   * @returns {string} URL formatada
   */
  formatarUrlLP(url) {
    if (!url || typeof url !== 'string') {
      return url;
    }

    // Remover espaços em branco
    let formattedUrl = url.trim();

    // Garantir que começa com https://
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }

    // Forçar HTTPS se for HTTP
    if (formattedUrl.startsWith('http://')) {
      formattedUrl = formattedUrl.replace('http://', 'https://');
    }

    // Remover barras duplas no path (exceto após https://)
    formattedUrl = formattedUrl.replace(/https:\/\//, 'HTTPS_PROTOCOL_PLACEHOLDER');
    formattedUrl = formattedUrl.replace(/\/+/g, '/');
    formattedUrl = formattedUrl.replace('HTTPS_PROTOCOL_PLACEHOLDER', 'https://');

    // Remover barra final se existir
    formattedUrl = formattedUrl.replace(/\/$/, '');

    return formattedUrl;
  }

  /**
   * Formata URL da PWA para garantir formato correto
   * @param {string} url - URL original
   * @returns {string} URL formatada
   */
  formatarUrlPWA(url) {
    if (!url || typeof url !== 'string') {
      return url;
    }

    // Remover espaços em branco
    let formattedUrl = url.trim();

    // Garantir que começa com https://
    if (!formattedUrl.startsWith('http://') && !formattedUrl.startsWith('https://')) {
      formattedUrl = 'https://' + formattedUrl;
    }

    // Forçar HTTPS se for HTTP
    if (formattedUrl.startsWith('http://')) {
      formattedUrl = formattedUrl.replace('http://', 'https://');
    }

    // Remover barras duplas no path (exceto após https://)
    formattedUrl = formattedUrl.replace(/https:\/\//, 'HTTPS_PROTOCOL_PLACEHOLDER');
    formattedUrl = formattedUrl.replace(/\/+/g, '/');
    formattedUrl = formattedUrl.replace('HTTPS_PROTOCOL_PLACEHOLDER', 'https://');

    // Remover barra final se existir
    formattedUrl = formattedUrl.replace(/\/$/, '');

    // Adicionar /login no final
    formattedUrl += '/login';

    return formattedUrl;
  }

  /**
   * Mostra erro usando SweetAlert2
   * @param {Object} error - Objeto de erro
   * @param {number} error.status - Status do erro
   * @param {string} error.msg - Mensagem do erro
   */
  showError(error) {
    // Verificar se o SweetAlert2 está disponível através do Vue prototype
    if (typeof Vue !== 'undefined' && Vue.prototype.$swal) {
      Vue.prototype.$swal({
        type: 'error',
        title: 'Erro na Autenticação',
        text: error.msg || 'Ocorreu um erro durante a autenticação SSO',
        timer: 3000,
        showConfirmButton: false
      })
    } else {
      console.error('SSO Plugin Error:', error)
      // Fallback para alert nativo se SweetAlert2 não estiver disponível
      alert(`Erro na Autenticação: ${error.msg || 'Ocorreu um erro durante a autenticação SSO'}`)
    }
  }

  /**
   * Mostra sucesso usando SweetAlert2
   * @param {Object} success - Objeto de sucesso
   * @param {string} success.msg - Mensagem de sucesso
   */
  showSuccess(success) {
    // Verificar se o SweetAlert2 está disponível através do Vue prototype
    if (typeof Vue !== 'undefined' && Vue.prototype.$swal) {
      Vue.prototype.$swal({
        type: 'success',
        title: 'Sucesso',
        text: success.msg,
      })
    } else {
      console.log('SSO Plugin Success:', success)
    }
  }
}

export default {
  install(Vue, options = {}) {
    // Criar instância do plugin
    const ssoPlugin = new SSOPlugin(Vue.prototype.$store || options.store)
    
    // Adicionar ao prototype do Vue
    Vue.prototype.$sso = ssoPlugin
    
    // Adicionar como propriedade global
    Vue.sso = ssoPlugin
    
    // Adicionar mixin para facilitar o uso em componentes
    Vue.mixin({
      methods: {
        /**
         * Método de conveniência para login SSO
         * @param {string} cliente - Identificação do cliente
         * @param {string} ambiente - Ambiente ('PRODUCAO' ou 'TESTE')
         * @param {Object} options - Opções adicionais
         */
        async loginSSO(cliente, ambiente, options = {}) {
          return await this.$sso.login({ cliente, ambiente, options })
        },

        /**
         * Método de conveniência para verificar se está carregando
         * @returns {boolean} True se estiver carregando
         */
        isSSOLoading() {
          return this.$sso.isLoading()
        }
      }
    })
  }
}   